import { Effect } from 'effect';
import { sessionAssetEffect } from '../client';
import { decodeStructuredDataEffect } from '../structured-schema';
import type { DocumentAssetStatus, OkraSession, StructuredSchema } from '../types';
import { okraHookFailure, type OkraHookError } from './hook-io';

/** One interruptible fetch/decode/poll program, owned by the committed React request. */
export function pollAssetQueryEffect<T>(
  session: OkraSession,
  asset: string,
  schema: StructuredSchema<T>,
  pollInterval: number,
  onPending: (status: DocumentAssetStatus) => void,
): Effect.Effect<T, OkraHookError> {
  const failure = (cause: unknown) =>
    okraHookFailure('assetQuery', 'Document asset query failed', cause);
  // Preserve the previous setTimeout behavior for out-of-range/zero delays.
  const delay =
    Number.isFinite(pollInterval) && pollInterval >= 1 && pollInterval <= 2147483647
      ? Math.trunc(pollInterval)
      : 1;
  return Effect.gen(function* () {
    while (true) {
      const result = yield* sessionAssetEffect<T>(session, asset).pipe(
        Effect.mapError((error) => failure(error.cause)),
      );
      if (result?.status === 'completed') {
        return yield* decodeStructuredDataEffect(schema, result.data).pipe(
          Effect.mapError((error) =>
            failure(
              new Error(`Asset "${asset}" failed schema validation: ${error.issues.join('; ')}`),
            ),
          ),
        );
      }
      if (result?.status === 'failed') {
        return yield* failure(new Error(result.error || `Asset "${asset}" failed.`));
      }
      yield* Effect.sync(() => onPending(result?.status ?? 'pending'));
      yield* Effect.sleep(delay);
    }
  });
}
